import { collection, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { classToFirestore } from "../models/class";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import SessionService from "./session_service";
import EnrollmentService from "./enrollment";
import { toISODate } from "../utils/date";
import { COLLECTIONS } from "../services/collections";

/**
 * ClassService
 * Centraliza as operações de banco de dados para o módulo de Turmas.
 * Coleção: tenants/{idTenant}/classes
 * 
 * REGRA DE NEGÓCIO:
 * Cada documento = 1 turma em 1 dia da semana.
 * Se o usuário selecionar Seg/Qua/Sex, o service cria 3 documentos separados.
 * 
 * INTEGRAÇÃO COM SESSÕES:
 * - CREATE: Gera sessões automáticas para 6 meses via SessionService.
 * - UPDATE: Atualiza apenas sessões a partir da data informada (fromDate).
 * - DELETE: Verifica matrículas; se houver, bloqueia. Senão, remove sessões futuras.
 */
const ClassService = {
    /**
     * Busca todas as turmas de um tenant.
     */
    getClasses: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.CLASSES),
                orderBy("startTime", "asc")
            );
            const querySnapshot = await getDocs(q);
            const classes = [];
            querySnapshot.forEach((docSnap) => {
                classes.push({ id: docSnap.id, ...docSnap.data() });
            });
            return classes;
        } catch (error) {
            console.error("Erro ao listar turmas:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma turma.
     * 
     * CREATE: Se weekdays tiver N dias, cria N documentos (um por dia) via batch.
     *         Após criar, gera sessões automáticas para 6 meses.
     * UPDATE: Atualiza um único documento existente.
     *         Após atualizar, propaga mudanças para sessões futuras.
     * 
     * @param {Object} formData - Dados do formulário (weekdays[] para criação)
     * @param {string} idTenant - ID do tenant
     * @param {Object} authUser - Usuário autenticado
     * @param {Object} lookups - { activities, employees, areas }
     * @param {Array} existingClasses - Lista atual para diff de auditoria
     * @param {string} [fromDate] - Para edição: data a partir da qual sessões futuras serão atualizadas (ISO YYYY-MM-DD)
     */
    save: async (formData, idTenant, authUser, lookups = {}, existingClasses = [], fromDate = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            if (formData.id) {
                // ─── UPDATE (documento único) ───
                const weekday = formData.weekday !== undefined ? formData.weekday : (formData.weekdays?.[0] ?? 0);
                const classData = classToFirestore(formData, weekday, idTenant, authUser, lookups);
                const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, formData.id);
                const oldClass = existingClasses.find(c => c.id === formData.id) || {};
                await updateDoc(classRef, classData);

                // ─── PROPAGAR PARA SESSÕES FUTURAS ───
                const editFromDate = fromDate || toISODate(new Date());

                // Se algum campo que afeta o cálculo das datas de sessão foi alterado,
                // precisamos APAGAR as próximas sessões e RECRIAR, 
                // em vez de apenas dar update nas que já existem (senão podem ficar em dias velhos).
                const dateRelatedKeys = ["weekday", "startDate", "endDate"];
                const datesChanged = dateRelatedKeys.some(key => oldClass[key] !== classData[key]);

                try {
                    if (datesChanged) {
                        // Deleta as sessões futuras (a partir da data informada, preservando as antigas)
                        await SessionService.deleteSessionsFromDate(idTenant, formData.id, editFromDate);

                        // Garante que a data de geração comece a partir da data informada ou do startDate da turma
                        const genStart = classData.startDate && classData.startDate > editFromDate
                            ? classData.startDate : editFromDate;

                        await SessionService.generateSessionsForClass(
                            { ...classData, startDate: genStart },
                            formData.id,
                            idTenant,
                            null
                        );
                    } else {
                        await SessionService.updateSessionsFromDate(
                            idTenant,
                            formData.id,
                            editFromDate,
                            classData
                        );
                    }
                } catch (sessionError) {
                    console.error("[ClassService] Erro ao atualizar sessões futuras:", sessionError);
                    // Não falha a operação principal
                }

                // Auditoria
                const changes = AuditService.calculateDiff(oldClass, classData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.CLASSES,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: classData.activityName,
                    description: `Atualizou a turma "${classData.activityName}" (${classData.startTime})`,
                    details: changes
                });

                return [{ id: formData.id, ...classData }];
            } else {
                // ─── CREATE (um documento por dia da semana) ───
                const weekdays = formData.weekdays || [];
                if (weekdays.length === 0) throw new Error("Selecione ao menos um dia da semana.");

                const batch = writeBatch(db);
                const newDocs = [];

                // Gerar ID de grupo para conectar turmas criadas juntas (Seg/Qua/Sex = mesmo grupo)
                const classGroupId = doc(collection(db, "tenants", idTenant, COLLECTIONS.CLASSES)).id;

                for (const weekday of weekdays) {
                    const classData = classToFirestore({ ...formData, classGroupId }, weekday, idTenant, authUser, lookups);
                    const newDocRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.CLASSES));
                    batch.set(newDocRef, classData);
                    newDocs.push({ id: newDocRef.id, weekday, ...classData });
                }

                await batch.commit();

                // ─── GERAR SESSÕES PARA CADA TURMA CRIADA ───
                let totalSessions = 0;
                for (const newClass of newDocs) {
                    try {
                        const count = await SessionService.generateSessionsForClass(
                            newClass,
                            newClass.id,
                            idTenant,
                            [] // Turma recém-criada: 0 matrículas, evita query ao Firestore
                        );
                        totalSessions += count;
                    } catch (sessionError) {
                        console.error(`[ClassService] Erro ao gerar sessões para turma ${newClass.id}:`, sessionError);
                    }
                }

                // Auditoria (log único para o lote)
                const sampleData = newDocs[0] || {};
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.CLASSES,
                    action: AUDIT_ACTIONS.CREATE,
                    entityName: sampleData.activityName,
                    description: `Criou ${newDocs.length} turma(s) "${sampleData.activityName}" (${sampleData.startTime}) nos dias: ${weekdays.join(", ")} — ${totalSessions} sessões geradas`,
                    details: { 
                        turmasGeradas: newDocs.map(d => d.id), 
                        atividade: sampleData.activityName || "-",
                        diasSemana: weekdays, 
                        totalSessoes: totalSessions, 
                        groupId: classGroupId 
                    }
                });

                return newDocs;
            }
        } catch (error) {
            console.error("Erro no ClassService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma turma e suas sessões futuras.
     * 
     * REGRAS:
     * 1. Verifica se há alunos matriculados. Se houver, retorna erro com lista.
     * 2. Remove sessões a partir da data informada (fromDate).
     * 3. Remove o documento da turma.
     * 
     * @param {string} classId - ID da turma
     * @param {string} idTenant - ID do tenant
     * @param {Object} authUser - Usuário autenticado
     * @param {string} className - Nome da turma (para auditoria)
     * @param {string} [fromDate] - Data a partir da qual excluir sessões (ISO YYYY-MM-DD)
     */
    delete: async (classId, idTenant, authUser, className, fromDate = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            // ─── 1. VERIFICAR MATRÍCULAS ───
            const enrolledMembers = await EnrollmentService.getEnrolledMembersByClass(idTenant, classId);
            if (enrolledMembers.length > 0) {
                const names = enrolledMembers.map(m => m.fullName).join(", ");
                const error = new Error(
                    `Não é possível excluir esta turma. Os seguintes alunos estão matriculados: ${names}`
                );
                error.code = "ENROLLED_MEMBERS";
                error.members = enrolledMembers;
                throw error;
            }

            // ─── 2. REMOVER SESSÕES FUTURAS ───
            const deleteFromDate = fromDate || toISODate(new Date());
            try {
                await SessionService.deleteSessionsFromDate(
                    idTenant,
                    classId,
                    deleteFromDate
                );
            } catch (sessionError) {
                console.error("[ClassService] Erro ao remover sessões:", sessionError);
            }

            // ─── 3. REMOVER A TURMA ───
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, classId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.CLASSES,
                action: AUDIT_ACTIONS.DELETE,
                entityId: classId,
                entityName: className,
                description: `Excluiu a turma "${className || classId}" e suas sessões a partir de ${deleteFromDate}`,
                details: { classOldId: classId, dataCancelamentoSessoes: deleteFromDate }
            });

            return true;
        } catch (error) {
            if (error.code === "ENROLLED_MEMBERS") throw error;
            console.error("Erro no ClassService.delete:", error);
            throw error;
        }
    }
};

export default ClassService;
